import moment from 'moment';

import {
  formatPhone,
  isValidPhone,
  parsePhone,
} from '../../../../../../util/phone';
import { isAddressValid } from '../../../../../../util/account';
import { ALBUM_FOLDER_MEMBERS_IMPORT_MODE } from '../../../AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembersCSVUploadModal/constants';
import {
  HIERARCHY_GRADES_ORDINAL_ENUM,
  HIERARCHY_GRADES_ENUM,
  HIERARCHY_GRADES_NUMBERED_ENUM,
} from '../../../../../../constants/hierarchy-grades';
import { IDENTITY_GENDER } from '../../../../../../constants/identity';
import { isValidEmail } from '../../../../../../util/email';

export const CSV_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Validates if a URL points to an image file based on its extension
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL ends with a valid image extension
 */

export const isValidImageUrl = url => {
  if (!url || typeof url !== 'string') return false;

  const imageExtensionPattern = /\.(jpg|jpeg|png|heic|heif)$/i;
  return url.match(imageExtensionPattern) !== null;
};

/**
 * A validated roster identity object containing all errors and basic info.
 *
 * @typedef {Object} ValidatedRosterIdentity An already validated roster identity.
 * @property {Object} rosterIdentity The roster identity object.
 * @property {Number} index Array index.
 * @property {Number} rowNumber The row number (base 1).
 * @property {Array<String>} errorFields List of invalid fields.
 * @property {String} [error] The error string (if any).
 */

/**
 * Output for the validateRosterIdentities method.
 *
 * @see validateRosterIdentities
 * @typedef {Object} ValidateRosterIdentitiesOutput
 * @property {Array<Object>} validRosterIdentities List of valid roster identities as an array of Roster Identities.
 * @property {Array<ValidatedRosterIdentity>} invalidRosterIdentities List of invalid roster identities as a ValidatedRosterIdentity object.
 */

/**
 * Validates a list of GQL-Ready roster identities. These identities must be ready
 * to be passed onto the create / edit gql methods directly.
 *
 * It will be assumed that these identity objects have unique ids, so that they can
 * later on be displayed and modified dynamically.
 *
 * @function validateRosterIdentities
 * @param {Object} opts Options object.
 * @param {Array<Object>} opts.rosterIdentities List of roster identities.
 * @param {{ phones: Array<String>, addresses: Array<String>, names: Array<String> }} opts.invalidItems Object containing arrays of invalid items (Addresses, phones and names). Can be used to invalidate identities regardless of the input's validity.
 * @param {String} opts.importMode the import mode, either general or uniform
 * @param {Boolean} opts.requiresAddress whether or not this import requires an address to continue.
 * @param {Boolean }opts.isYearbookEvent wether the event for which we're valldating is a yearbook event or not
 * @param {Boolean} opts.ignoreContactInformationErrors whether or not imports ignore lack of contact info (phone, emails) when erroring out.
 * @returns {ValidateRosterIdentitiesOutput} Object containing invalid & valid roster identities.
 */
export const validateRosterIdentities = ({
  rosterIdentities = [],
  invalidItems = {
    phones: [],
    addresses: [],
    names: [],
    email_addresses: [],
    ref_ids: [],
  },
  importMode = ALBUM_FOLDER_MEMBERS_IMPORT_MODE.GENERAL,
  requiresAddress = false,
  ignoreContactInformationErrors = false,
  isYearbookEvent = false,
}) => {
  const seenMemberRefIds = [];
  // here we validate each and every roster identity before we attempt to create them.
  const validatedRosterIdentities = rosterIdentities.map(
    (rosterIdentity, index) => {
      const rosterIdentityErrors = [];
      const {
        first_name: firstName,
        last_name: lastName,
        email_addresses: emailAddresses,
        date_of_birth: dateOfBirth,
        linked_identities: linkedIdentities,
        jersey_number: jerseyNumber,
        organization_ref_id: refId,
        grade,
      } = rosterIdentity;

      const {
        phones = [],
        names = [],
        ref_ids: existingMemberRefIds = [],
      } = invalidItems || {};

      const rowNumber = index + 1;
      const errorFields = [];

      // Displays First + Last name if available, otherwise only first or last name.
      const fullName = `${firstName || ''}${
        firstName && lastName ? ` ${lastName || ''}` : lastName || ''
      }`;

      if (!firstName?.trim()) {
        errorFields.push('first_name');
        rosterIdentityErrors.push(
          `First name is a required value to create a new entry`,
        );
      }

      if (!lastName?.trim()) {
        errorFields.push('last_name');
        rosterIdentityErrors.push(
          `Last name is a required value to create a new entry`,
        );
      }

      if (names.includes(fullName)) {
        errorFields.push('first_name');
        errorFields.push('last_name');
        rosterIdentityErrors.push(`This name is invalid.`);
      }

      if (
        dateOfBirth &&
        !moment(dateOfBirth, CSV_DATE_FORMAT, true).isValid()
      ) {
        errorFields.push('date_of_birth');
        rosterIdentityErrors.push(
          `Provided date of birth (${dateOfBirth}) is invalid`,
        );
      }

      if (
        !emailAddresses.length &&
        !linkedIdentities.length &&
        !ignoreContactInformationErrors
      ) {
        errorFields.push('email_addresses');
        rosterIdentityErrors.push(
          `Phone number or email address(es) are required to create a new entry`,
        );
      }

      linkedIdentities.forEach(linkedIdentity => {
        const { phone_number: phoneNumber, addresses = [] } = linkedIdentity;
        const [address] = addresses;

        if (
          !emailAddresses?.length &&
          !phoneNumber &&
          !ignoreContactInformationErrors
        ) {
          errorFields.push('phone_number');
          rosterIdentityErrors.push(
            `Phone number or email address(es) are required to create a new entry`,
          );
          return;
        }

        // If we have emails, we can safely ignore this so long as there is no address to check.
        if (emailAddresses?.length && !address) {
          let hasErrors = false;
          emailAddresses.forEach(({ email_address: emailAddress }) => {
            if (!isValidEmail(emailAddress, true)) {
              errorFields.push(`email_addresses_${emailAddress}`);
              rosterIdentityErrors.push(
                `Email address ${emailAddress} is not valid`,
              );
              hasErrors = true;
            }
          });

          if (hasErrors) {
            return;
          }
        }

        if (
          phoneNumber &&
          (!isValidPhone(phoneNumber) ||
            // Here we parse the phone to remove any + it might get.
            // React-telephone input may add it randomly, so better safe than sorry.
            phones.includes(parsePhone(phoneNumber)))
        ) {
          errorFields.push(`phone_number_${phoneNumber}`);
          rosterIdentityErrors.push(
            `Phone number ${formatPhone(phoneNumber)} is not valid`,
          );
        }

        if (address || requiresAddress) {
          // Validate address. This is basically how GQL does it.
          // So we should avoid passing an invalid address at any point.
          if (!address) {
            errorFields.push('address');
            rosterIdentityErrors.push(`Address is required`);
          } else if (!isAddressValid(address)) {
            errorFields.push('address');
            rosterIdentityErrors.push(
              `Address ${address.address_line_1} is not valid`,
            );
          }
        }
      });

      const jerseyNumberRegexp = /^[0-9]{1,2}$/;
      if (
        importMode === ALBUM_FOLDER_MEMBERS_IMPORT_MODE.UNIFORM &&
        !jerseyNumberRegexp.test(`${jerseyNumber}`.trim())
      ) {
        errorFields.push('jersey_number');
        rosterIdentityErrors.push(
          'Jersey number invalid, must be a number between 00 and 99',
        );
      }

      const refIdStr = (refId ?? '').toString();
      const existingMember = seenMemberRefIds.find(
        ref => ref.organization_ref_id === refIdStr,
      );

      if (refIdStr) {
        if (
          !existingMember &&
          (existingMemberRefIds.length === 0 ||
            !existingMemberRefIds.includes(refIdStr))
        ) {
          seenMemberRefIds.push({
            first_name: firstName.trim().toLowerCase(),
            last_name: lastName.trim().toLowerCase(),
            organization_ref_id: refIdStr,
          });
        } else if (
          (refIdStr ?? '').toUpperCase() !== 'STAFF' &&
          (existingMemberRefIds.includes(refIdStr) ||
            existingMember.first_name !== firstName.trim().toLowerCase() ||
            existingMember.last_name !== lastName.trim().toLowerCase())
        ) {
          errorFields.push('organization_ref_id');
          rosterIdentityErrors.push(
            'The ID provided is already in use by another member',
          );
        }
      } else if (
        isYearbookEvent &&
        grade !== HIERARCHY_GRADES_ENUM.STAFF &&
        !refIdStr
      ) {
        errorFields.push('organization_ref_id');
        rosterIdentityErrors.push(
          'Ref ID is required for students and members outside of STAFF',
        );
      }

      const error = `Row (${rowNumber}) - ${fullName}: ${rosterIdentityErrors.join(
        ', ',
      )}`;

      return {
        index,
        rowNumber,
        rosterIdentity,
        error: rosterIdentityErrors.length === 0 ? undefined : error,
        errorFields,
      };
    },
  );

  const validRosterIdentities = validatedRosterIdentities
    .filter(validatedRosterIdentity => !validatedRosterIdentity.error)
    .map(({ rosterIdentity }) => rosterIdentity);

  const invalidRosterIdentities = validatedRosterIdentities.filter(
    validatedRosterIdentity => !!validatedRosterIdentity.error,
  );

  return {
    validRosterIdentities,
    invalidRosterIdentities,
  };
};

/**
 * Given a grade string, translate into a hierarchyGrade enum value.
 *
 * @param {String} grade the grade to translate
 * @returns {undefined|*}
 */
export const getRosterIdentityGradeEnum = grade => {
  if (!grade) {
    return undefined;
  }

  // Ensure grade is not a number by stringifying it.
  const stringGrade = `${grade ?? ''}`;
  const enumGradeLabel = stringGrade
    .toUpperCase()
    .replace(' ', '_')
    .replace('-', '_')
    .trim();

  const ordinalEnumGradeLabel = stringGrade.toLowerCase().trim();

  return (
    HIERARCHY_GRADES_ENUM?.[enumGradeLabel] ??
    HIERARCHY_GRADES_ORDINAL_ENUM?.[ordinalEnumGradeLabel] ??
    HIERARCHY_GRADES_NUMBERED_ENUM?.[stringGrade]
  );
};

export const getRosterIdentityGenderEnum = gender => {
  if (!gender) {
    return undefined;
  }

  if (['f', 'female', 'girl', 'girls'].includes(gender.toLowerCase().trim())) {
    return IDENTITY_GENDER.FEMALE;
  }

  if (['m', 'male', 'boy', 'boys'].includes(gender.toLowerCase().trim())) {
    return IDENTITY_GENDER.MALE;
  }

  return undefined;
};
